import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, X, Send, Sparkles, RefreshCw,
  ChevronDown, Shield, AlertTriangle, Zap, Network, ExternalLink,
} from 'lucide-react';
import { apiClient } from '../../services/api/client';
import { fmtDate, fmtDateTime, fmtTime } from '../../utils/date';
import { navigateToGraph } from '../../utils/graphNav';
import { useAIAdvisor } from '../../contexts/AIAdvisorContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GraphNode {
  id: string;
  type?: string;
  node_type?: string;
  label?: string;
  severity?: string;
  risk_level?: string;
  provider?: string;
  [key: string]: unknown;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  ts: number;
  error?: boolean;
  // graph query result
  graphResult?: {
    nodes: GraphNode[];
    summary: string;
    graph_url?: string;
    filters: Record<string, unknown>;
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────


const QUICK_PROMPTS = [
  { icon: <AlertTriangle className="w-3 h-3" />, text: 'What are my most critical findings?' },
  { icon: <Shield className="w-3 h-3" />,        text: 'How do I fix a public S3 bucket?' },
  { icon: <Network className="w-3 h-3" />,       text: 'Show internet-facing EC2 with critical CVEs', isGraph: true },
  { icon: <Network className="w-3 h-3" />,       text: 'Find overprivileged IAM roles in AWS', isGraph: true },
  { icon: <Network className="w-3 h-3" />,       text: 'List S3 buckets without encryption', isGraph: true },
  { icon: <Network className="w-3 h-3" />,       text: 'Find RDS instances publicly accessible', isGraph: true },
  { icon: <Zap className="w-3 h-3" />,           text: 'Explain attack paths' },
  { icon: <Shield className="w-3 h-3" />,        text: 'How do I reduce my blast radius?' },
  // Compliance-aware prompts (Mika AI equivalent)
  { icon: <Shield className="w-3 h-3" />,        text: 'Which frameworks are we failing?', isCompliance: true },
  { icon: <Shield className="w-3 h-3" />,        text: 'What controls fail for PCI-DSS?', isCompliance: true },
  { icon: <Zap className="w-3 h-3" />,           text: 'Show cross-framework remediation priorities', isCompliance: true },
  { icon: <Shield className="w-3 h-3" />,        text: 'What is our SOC 2 compliance score?', isCompliance: true },
];

// Compliance query detection — queries about compliance posture, frameworks, controls
const COMPLIANCE_QUERY_PATTERNS = [
  /compliance\s+(score|posture|status|framework|control)/i,
  /which\s+(framework|control|standard)s?\s+(are|is)\s+(we\s+)?(fail|pass)/i,
  /(pci|hipaa|soc\s*2|nist|cis|iso|gdpr|fedramp|hitrust)\s+(compliance|score|status|control|fail)/i,
  /cross.framework/i,
  /remediation\s+priorit/i,
  /audit\s+(evidence|board|report)/i,
  /what\s+controls?\s+(fail|pass)/i,
];

// Graph query detection — queries that should call /graph-query instead of /chat
const GRAPH_QUERY_PATTERNS = [
  /^(show|find|list|which|get|what)\s+(all\s+)?/i,
  /internet.facing/i,
  /public\s+(s3|bucket|rds|database|instance)/i,
  /overprivileged/i,
  /critical\s+cve/i,
  /without\s+(encryption|mfa|logging|monitoring)/i,
  /resources?\s+(with|in|that)/i,
  /unencrypted/i,
  /exposed\s+(port|service|endpoint)/i,
  /admin\s+(access|role|permission)/i,
  /publicly\s+accessible/i,
];

function isGraphQuery(text: string): boolean {
  return GRAPH_QUERY_PATTERNS.some(p => p.test(text));
}

// Map the current route to a coarse page-context key. Used both to fetch
// context-aware starter prompts (`/assistant/suggestions?context=`) and to
// ground every chat request in what the user is currently looking at, so the
// backend no longer has to guess the page from the message text alone.
function derivePageKey(path: string): string {
  if (path.startsWith('/issues') || path.startsWith('/findings') || path.startsWith('/threats') || path.startsWith('/defend'))
    return 'findings';
  if (path.startsWith('/compliance')) return 'compliance';
  if (path.startsWith('/ciem') || path.startsWith('/identity')) return 'iam';
  if (path.startsWith('/inventory') || path.startsWith('/resource')) return 'resources';
  if (path === '/' || path.startsWith('/overview')) return 'dashboard';
  return 'general';
}

// Severity colour helper
function sevColor(sev?: string): string {
  switch ((sev || '').toLowerCase()) {
    case 'critical': return 'text-red-400';
    case 'high':     return 'text-orange-400';
    case 'medium':   return 'text-yellow-400';
    case 'low':      return 'text-blue-400';
    default:         return 'text-gray-400';
  }
}

// ─── Markdown-lite renderer ───────────────────────────────────────────────────
// Converts common markdown to JSX without importing a library.

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let i = 0;
  let keyCounter = 0;

  const key = () => `md-${keyCounter++}`;

  const inlineFormat = (line: string): React.ReactNode => {
    // Bold, code, links (basic)
    const parts = line.split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('`') && part.endsWith('`'))
        return <code key={idx} className="bg-gray-800 text-wiz-purple px-1 py-0.5 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
      if (part.startsWith('**') && part.endsWith('**'))
        return <strong key={idx} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      return part;
    });
  };

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={key()} className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs font-mono text-green-300 overflow-x-auto my-2 whitespace-pre-wrap">
          {codeLines.join('\n')}
        </pre>
      );
      i++;
      continue;
    }

    // Table
    if (line.includes('|') && lines[i + 1]?.includes('---')) {
      const headers = line.split('|').map(h => h.trim()).filter(Boolean);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|')) {
        rows.push(lines[i].split('|').map(c => c.trim()).filter(Boolean));
        i++;
      }
      elements.push(
        <div key={key()} className="overflow-x-auto my-2">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-gray-700">
                {headers.map((h, hi) => (
                  <th key={hi} className="text-left py-1.5 px-2 text-gray-400 font-semibold">{inlineFormat(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-b border-gray-800/50">
                  {row.map((cell, ci) => (
                    <td key={ci} className="py-1.5 px-2 text-gray-300">{inlineFormat(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Heading
    if (line.startsWith('### '))
      { elements.push(<h4 key={key()} className="text-white font-semibold text-xs uppercase tracking-wide mt-3 mb-1">{line.slice(4)}</h4>); i++; continue; }
    if (line.startsWith('## '))
      { elements.push(<h3 key={key()} className="text-wiz-purple font-bold text-sm mt-3 mb-1.5">{line.slice(3)}</h3>); i++; continue; }
    if (line.startsWith('# '))
      { elements.push(<h2 key={key()} className="text-white font-bold text-base mt-2 mb-2">{line.slice(2)}</h2>); i++; continue; }

    // List item
    if (/^[-*] /.test(line) || /^\d+\. /.test(line)) {
      const listItems: string[] = [];
      const ordered = /^\d+\. /.test(line);
      while (i < lines.length && (/^[-*] /.test(lines[i]) || /^\d+\. /.test(lines[i]))) {
        listItems.push(lines[i].replace(/^[-*] /, '').replace(/^\d+\. /, ''));
        i++;
      }
      const Tag = ordered ? 'ol' : 'ul';
      elements.push(
        <Tag key={key()} className={`my-1.5 pl-4 space-y-0.5 text-gray-300 text-xs ${ordered ? 'list-decimal' : 'list-disc'}`}>
          {listItems.map((item, li) => <li key={li}>{inlineFormat(item)}</li>)}
        </Tag>
      );
      continue;
    }

    // Blank line
    if (line.trim() === '') { elements.push(<div key={key()} className="h-1.5" />); i++; continue; }

    // Paragraph
    elements.push(<p key={key()} className="text-gray-300 text-xs leading-relaxed">{inlineFormat(line)}</p>);
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

// ─── Typing dots ──────────────────────────────────────────────────────────────

const TypingDots: React.FC = () => (
  <div className="flex items-center gap-1 py-1">
    {[0, 1, 2].map(i => (
      <span
        key={i}
        className="w-1.5 h-1.5 bg-wiz-purple rounded-full animate-bounce"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

// ─── Main widget ──────────────────────────────────────────────────────────────

const AIAssistantWidget: React.FC<{ onNavigate?: (path: string) => void }> = ({ onNavigate }) => {
  const [isOpen, setIsOpen]     = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [hasNew, setHasNew]     = useState(false);
  const [serviceOk, setServiceOk] = useState<boolean | null>(null);
  // R101: context-aware suggestions from BE `/api/v1/assistant/suggestions`.
  // Replaces hardcoded QUICK_PROMPTS with categorized starter prompts chosen
  // by current route (dashboard / findings / iam / etc.). Falls back to the
  // hardcoded list if the BE call fails.
  type BESuggestion = { id?: string; text: string; category?: string; icon?: string; dynamic?: boolean; count?: number };
  const [beSuggestions, setBeSuggestions] = useState<BESuggestion[] | null>(null);

  // Item #4: the advisor's grounding focus — the finding/resource the user
  // asked about (via `askAbout`) or that the current detail page registered.
  const { entity, clearEntity } = useAIAdvisor();

  const listRef  = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Build the grounding context sent with every request: the current page
  // key plus, when set, the focused entity's id + a snapshot of real fields
  // the caller already loaded. The backend fetches the live record by id for
  // freshness but falls back to this snapshot, so grounding never depends on
  // a maybe-404 endpoint.
  const buildContext = useCallback(() => {
    const ctx: Record<string, unknown> = { page: derivePageKey(window.location.pathname) };
    if (entity) {
      if (entity.kind === 'finding') ctx.finding_id = entity.id;
      if (entity.kind === 'resource') ctx.resource_id = entity.id;
      ctx.entity_kind = entity.kind;
      if (entity.label) ctx.label = entity.label;
      if (entity.snapshot) ctx.snapshot = entity.snapshot;
    }
    return ctx;
  }, [entity]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
      setHasNew(false);
    }
  }, [isOpen]);

  // Listen for external open trigger (from TopNav button)
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-ai-assistant', handler);
    return () => window.removeEventListener('open-ai-assistant', handler);
  }, []);

  // Escape key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) setIsOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  // R101: load context-aware suggestions when widget opens.
  // Path-prefix → BE context key. BE handler at ai-assistant/main.py:1659
  // narrows the 12-suggestion catalog by category map (dashboard/findings/iam/etc).
  useEffect(() => {
    if (!isOpen || beSuggestions !== null) return;
    const ctx = derivePageKey(window.location.pathname);
    apiClient.request<any>(`/api/v1/assistant/suggestions?context=${encodeURIComponent(ctx)}`, {
      method: 'GET', timeoutMs: 5000, retry: false,
    }).then(({ data, error }) => {
      if (error || !data) {
        setBeSuggestions([]);  // empty = fall back to QUICK_PROMPTS
        return;
      }
      const list = Array.isArray(data) ? data : (data.suggestions || []);
      setBeSuggestions(Array.isArray(list) ? list : []);
    }).catch(() => setBeSuggestions([]));
  }, [isOpen, beSuggestions]);

  // Health check on mount
  useEffect(() => {
    apiClient.request<any>('/health', { method: 'GET', timeoutMs: 3000, retry: false })
      .then(({ error }) => setServiceOk(!error))
      .catch(() => setServiceOk(false));
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    const userMsg: Message = { role: 'user', content: trimmed, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      // Route compliance queries to compliance API
      const isComplianceQuery = COMPLIANCE_QUERY_PATTERNS.some(p => p.test(trimmed));
      if (isComplianceQuery) {
        // Fetch compliance context and build a response
        const [postureResp, overlapResp] = await Promise.all([
          apiClient.get<any>('/api/v1/compliance/posture').catch(() => null),
          apiClient.get<any>('/api/v1/compliance/cross-framework/overlap').catch(() => null),
        ]);
        const postureRes: any = postureResp?.data || null;
        const overlapRes: any = overlapResp?.data || null;

        let complianceContext = '';
        if (postureRes) {
          complianceContext += `**Overall Compliance: ${postureRes.overall_score}%** (${postureRes.total_rules} rules, ${postureRes.total_frameworks} frameworks)\n\n`;
          const fwScores = (postureRes.framework_scores || []).sort((a: any, b: any) => a.score - b.score);
          if (fwScores.length > 0) {
            complianceContext += '**Framework Scores:**\n';
            fwScores.forEach((fs: any) => {
              const icon = fs.score >= 80 ? '✅' : fs.score >= 60 ? '⚠️' : '❌';
              complianceContext += `${icon} ${fs.framework_id}: ${fs.score}% (${fs.passed}/${fs.passed + fs.failed} passed)\n`;
            });
          }
        }
        if (overlapRes && overlapRes.stats) {
          complianceContext += `\n**Cross-Framework Impact:** ${overlapRes.total} rules overlap across frameworks. ${overlapRes.stats.rules_affecting_5plus_frameworks} rules affect 5+ frameworks — fixing these gives the biggest compliance improvement.\n`;
        }
        if (!complianceContext) complianceContext = 'Compliance data is loading. Please try again in a moment.';

        // Also send to chat for AI interpretation
        const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
        const { data: chatData } = await apiClient.request<any>('/api/v1/assistant/chat', {
          method: 'POST',
          body: { message: `${trimmed}\n\nCompliance Context:\n${complianceContext}`, history, context: buildContext() },
          timeoutMs: 30000,
        }).catch(() => ({ data: null }));

        const reply = (chatData as any)?.reply || complianceContext;
        const aiMsg: Message = { role: 'assistant', content: reply, ts: Date.now() };
        setMessages(prev => [...prev, aiMsg]);
        if (!isOpen) setHasNew(true);
        return;
      }

      // Route to graph-query endpoint for resource-search queries
      if (isGraphQuery(trimmed)) {
        const { data: dataResp, error, status } = await apiClient.request<any>('/api/v1/assistant/graph-query', {
          method: 'POST',
          body: { query: trimmed, context: buildContext() },
          timeoutMs: 15000,
        });
        if (error || !dataResp) throw new Error(`HTTP ${status}`);
        const data = dataResp as any;
        const aiMsg: Message = {
          role: 'assistant',
          content: data.summary,
          ts: Date.now(),
          graphResult: {
            nodes: data.nodes || [],
            summary: data.summary,
            graph_url: data.graph_url,
            filters: data.filters || {},
          },
        };
        setMessages(prev => [...prev, aiMsg]);
        if (!isOpen) setHasNew(true);
        return;
      }

      // Regular chat
      const history = messages.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const { data: chatRespData, error: chatError, status: chatStatus } = await apiClient.request<any>('/api/v1/assistant/chat', {
        method: 'POST',
        body: { message: trimmed, history, context: buildContext() },
        timeoutMs: 30000,
      });

      if (chatError || !chatRespData) throw new Error(`HTTP ${chatStatus}`);
      const data = chatRespData as any;

      const aiMsg: Message = { role: 'assistant', content: data.reply, ts: Date.now() };
      setMessages(prev => [...prev, aiMsg]);
      if (!isOpen) setHasNew(true);

    } catch (err) {
      const errMsg: Message = {
        role: 'assistant',
        content: serviceOk === false
          ? '⚠️ The AI Assistant service is offline. Start it with:\n```\npython services/ai-assistant/app/main.py\n```'
          : '⚠️ Failed to get a response. Please try again.',
        ts: Date.now(),
        error: true,
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, isOpen, serviceOk, buildContext]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const clearChat = () => setMessages([]);

  return (
    <>
      {/* ── Chat panel ─────────────────────────────────────────────────────── */}
      <div
        className={`
          fixed bottom-24 right-6 z-50 w-96 flex flex-col
          bg-wiz-darker border border-gray-800 rounded-2xl shadow-2xl
          transition-all duration-300 ease-out
          ${isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-6 pointer-events-none'
          }
        `}
        style={{ height: 520 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-wiz-purple/20 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-wiz-purple" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-none">AI Security Assistant</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {serviceOk === false ? '● Offline' : serviceOk === true ? '● Ready' : '● Connecting…'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                title="Clear chat"
                className="p-1.5 rounded-lg text-gray-600 hover:text-gray-300 hover:bg-gray-800 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-gray-600 hover:text-gray-300 hover:bg-gray-800 transition-colors"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Grounding chip — shows the finding/resource the advisor is focused
            on. Every request carries this context; clearing it drops the
            advisor back to page-level grounding. */}
        {entity && (
          <div className="shrink-0 flex items-center gap-2 px-4 py-2 border-b border-gray-800 bg-wiz-purple/5">
            <Sparkles className="w-3 h-3 text-wiz-purple shrink-0" />
            <span className="text-xs text-gray-500 shrink-0">Grounded on</span>
            <span className="text-xs text-gray-300 truncate flex-1" title={entity.label || entity.id}>
              {entity.kind === 'finding' ? 'Finding' : 'Resource'}: {entity.label || entity.id}
            </span>
            <button
              onClick={clearEntity}
              title="Clear context"
              className="p-0.5 rounded text-gray-600 hover:text-gray-300 hover:bg-gray-800 transition-colors shrink-0"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Message list */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {messages.length === 0 && !loading ? (
            /* Empty state — quick prompts */
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-wiz-purple/10 border border-wiz-purple/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-wiz-purple" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-white">Ask me anything about security</p>
                <p className="text-xs text-gray-500 mt-1">CSPM · CWPP · CIEM · DSPM · CDR · Graph queries</p>
              </div>
              <div className="w-full space-y-1.5">
                {/* R101: prefer BE-driven context-aware suggestions; fall back
                    to hardcoded QUICK_PROMPTS when offline or BE returned 0. */}
                {(beSuggestions && beSuggestions.length > 0
                  ? beSuggestions.map(s => ({
                      icon: s.dynamic ? <AlertTriangle className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />,
                      text: s.text,
                      category: s.category,
                      dynamic: s.dynamic,
                    }))
                  : QUICK_PROMPTS.map(qp => ({ ...qp, category: undefined as string | undefined, dynamic: false as boolean | undefined }))
                ).map((qp, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(qp.text)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-center gap-2 ${
                      (qp as { dynamic?: boolean }).dynamic
                        ? 'border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60 text-amber-200 hover:text-amber-100'
                        : 'border-gray-800 hover:border-wiz-purple/40 hover:bg-wiz-purple/5 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    <span className={(qp as { dynamic?: boolean }).dynamic ? 'text-amber-400' : 'text-wiz-purple'}>{qp.icon}</span>
                    <span className="flex-1">{qp.text}</span>
                    {(qp as { isGraph?: boolean }).isGraph && (
                      <span className="text-xs text-wiz-purple/60 border border-wiz-purple/20 px-1 rounded">graph</span>
                    )}
                    {(qp as { dynamic?: boolean }).dynamic && (
                      <span className="text-xs text-amber-400 border border-amber-500/40 px-1 rounded">live</span>
                    )}
                    {qp.category && !(qp as { dynamic?: boolean }).dynamic && (
                      <span className="text-xs text-gray-500 border border-gray-700/60 px-1 rounded">{qp.category}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-lg bg-wiz-purple/20 flex items-center justify-center shrink-0 mt-0.5 mr-2">
                      {msg.graphResult
                        ? <Network className="w-3 h-3 text-wiz-purple" />
                        : <Sparkles className="w-3 h-3 text-wiz-purple" />
                      }
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-xl px-3 py-2 ${
                      msg.role === 'user'
                        ? 'bg-wiz-purple/20 border border-wiz-purple/30 text-gray-200 text-xs'
                        : msg.error
                          ? 'bg-red-500/10 border border-red-500/20 text-xs'
                          : 'bg-gray-800/60 border border-gray-700/50 text-xs'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <p className="text-xs leading-relaxed">{msg.content}</p>
                    ) : msg.graphResult ? (
                      /* Graph query result */
                      <div className="space-y-2">
                        {renderMarkdown(msg.content)}
                        {msg.graphResult.nodes.length > 0 && (
                          <div className="mt-2 space-y-1 max-h-36 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700">
                            {msg.graphResult.nodes.slice(0, 10).map((node, ni) => {
                              const sev = node.severity || node.risk_level;
                              const label = (node.label || node.id || '').toString().replace(/_/g, ' ');
                              const ntype = (node.type || node.node_type || '').toString().replace(/_/g, ' ');
                              return (
                                <div key={ni} className="flex items-center gap-2 px-2 py-1 rounded bg-gray-900/60 border border-gray-700/40">
                                  <span className={`text-xs font-bold uppercase ${sevColor(sev)}`}>{sev || '—'}</span>
                                  <span className="text-xs text-gray-300 truncate flex-1">{label}</span>
                                  <span className="text-xs text-gray-600 shrink-0">{ntype}</span>
                                </div>
                              );
                            })}
                            {msg.graphResult.nodes.length > 10 && (
                              <p className="text-xs text-gray-600 text-center">
                                +{msg.graphResult.nodes.length - 10} more — view all in Graph
                              </p>
                            )}
                          </div>
                        )}
                        <button
                          onClick={() => {
                            // Derive search term from first node or filter type
                            const firstNode = msg.graphResult!.nodes[0];
                            const searchTerm =
                              firstNode?.label ||
                              firstNode?.id ||
                              (msg.graphResult!.filters?.node_types as string[] | undefined)?.[0]?.replace(/_/g, ' ') ||
                              '';
                            navigateToGraph({ search_term: searchTerm, resource_name: searchTerm });
                            setIsOpen(false);
                          }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-wiz-purple/20 border border-wiz-purple/30 text-xs text-wiz-purple hover:bg-wiz-purple/30 transition-colors mt-1"
                        >
                          <ExternalLink className="w-2.5 h-2.5" />
                          View in Graph
                        </button>
                      </div>
                    ) : (
                      renderMarkdown(msg.content)
                    )}
                    <p className="text-gray-600 text-xs mt-1.5">
                      {fmtTime(String(msg.ts), { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="w-6 h-6 rounded-lg bg-wiz-purple/20 flex items-center justify-center shrink-0 mt-0.5 mr-2">
                    <Sparkles className="w-3 h-3 text-wiz-purple" />
                  </div>
                  <div className="bg-gray-800/60 border border-gray-700/50 rounded-xl px-3 py-2">
                    <TypingDots />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Input area */}
        <div className="shrink-0 border-t border-gray-800 p-3">
          <div className="flex items-end gap-2 bg-gray-800/50 border border-gray-700/50 rounded-xl px-3 py-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about findings, risks, or remediation…"
              rows={1}
              disabled={loading}
              className="flex-1 bg-transparent text-xs text-gray-200 placeholder-gray-600 resize-none outline-none leading-relaxed max-h-24 scrollbar-thin scrollbar-thumb-gray-700"
              style={{ minHeight: 20 }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
              className="shrink-0 w-7 h-7 rounded-lg bg-wiz-purple disabled:bg-gray-700 disabled:text-gray-600 text-white flex items-center justify-center transition-colors hover:bg-wiz-purple/80"
            >
              {loading
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Send className="w-3.5 h-3.5" />
              }
            </button>
          </div>
          <p className="text-xs text-gray-700 mt-1.5 text-center">Enter to send · Shift+Enter for newline · Esc to close</p>
        </div>
      </div>

      {/* ── Floating "Ask AI" button (Wiz-style) ─────────────────────────── */}
      <button
        onClick={() => setIsOpen(o => !o)}
        title="Ask AI"
        className={`
          fixed bottom-6 right-6 z-40 rounded-full shadow-2xl
          flex items-center gap-2 transition-all duration-200
          ${isOpen
            ? 'bg-gray-800 border border-gray-700 text-gray-300 px-4 py-3'
            : 'bg-wiz-purple hover:bg-wiz-purple/90 text-white px-5 py-3'
          }
        `}
      >
        {isOpen ? (
          <X className="w-4 h-4" />
        ) : (
          <>
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Ask AI</span>
          </>
        )}
        {/* Unread badge */}
        {hasNew && !isOpen && (
          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-wiz-border" />
        )}
      </button>
    </>
  );
};

export default AIAssistantWidget;
